import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import { Action } from "redux";
import { Link } from "react-router-dom";
import { ResponsivePie } from "@nivo/pie";
import { ResponsiveLine } from "@nivo/line";
import {
  Activity,
  CheckCircle2,
  Clock,
  XCircle,
  FileText,
  PieChart as PieChartIcon,
  TrendingUp,
  ArrowUpRight,
  ShieldCheck,
  Users
} from "lucide-react";

import { getLineChartData, getPieChartData, getCardData } from "../../store/actions";

type AppDispatch = ThunkDispatch<any, any, Action>;

const CHART_THEME = {
  axis: {
    domain: { line: { stroke: "#e2e8f0", strokeWidth: 1 } },
    legend: { text: { fontSize: 12, fontWeight: 600, fill: "#64748b" } },
    ticks: {
      line: { stroke: "#e2e8f0", strokeWidth: 1 },
      text: { fontSize: 11, fill: "#94a3b8" }
    }
  },
  grid: {
    line: { stroke: "#f1f5f9", strokeWidth: 1 }
  },
  legends: {
    text: { fontSize: 12, fill: "#64748b" }
  },
  tooltip: {
    container: {
      background: "#fff",
      boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1)",
      borderRadius: "0.75rem",
      fontSize: "12px",
      padding: "10px 14px",
      border: "1px solid #e2e8f0"
    }
  }
};

const Dashboard = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      dispatch(getPieChartData()),
      dispatch(getLineChartData()),
      dispatch(getCardData())
    ]).finally(() => {
      setLoading(false);
    });
  }, [dispatch]);

  const pieData = useSelector<any, any[]>(state => state.dashboard.pieChartData);
  const lineData = useSelector<any, any[]>(state => state.dashboard.lineChartData);
  const cardData = useSelector<any, any[]>(state => state.dashboard.cardData);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#8dc63f]/20 border-t-[#8dc63f]"></div>
      </div>
    );
  }

  // Process data for Enterprise Metrics
  const counts = {
    pending: cardData?.find(d => d._id === 'Pending')?.status || 0,
    activated: cardData?.find(d => d._id === 'Activated')?.status || 0,
    rejected: cardData?.find(d => d._id === 'Rejected')?.status || 0,
    total: cardData?.find(d => d._id === 'Total')?.status || 0
  };
  const totalFromPie = pieData?.reduce((acc: number, cur: any) => acc + cur.value, 0) ?? 0;

  const processed = counts.activated + counts.rejected;
  const tickValue = Math.max(1, Math.round((lineData?.[0]?.data?.length ?? 10) / 7));

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500 min-h-screen">
      {/* Header Section */}
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>

          <h2 className="text-2xl font-extrabold tracking-tight text-slate-900">Dashboard</h2>

        </div>
        {/* <div className="flex items-center gap-3 px-4 md:px-5 py-2 md:py-2.5 bg-white rounded-2xl border border-slate-200 shadow-sm text-xs md:text-sm font-bold text-slate-600 transition-all hover:border-[#d8f0b3] hover:shadow-md cursor-default w-fit">
          <div className="relative flex h-2 w-2 mr-1">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#a3d853] opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#8dc63f]"></span>
          </div>
          System Live
        </div> */}
      </header>

      {/* Enterprise Pro Metric Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
        {/* Active/Pending */}
        <Link to="/reports?status=Pending" className="group bg-white rounded-[16px] border border-slate-200 p-5 shadow-sm hover:shadow-md hover:border-amber-200/80 transition-all duration-300 relative overflow-hidden flex flex-col justify-between">
          <div className="relative z-10">
            <div className="flex items-start justify-between">
              <div>
                <span className="text-[10px] font-bold text-slate-600 uppercase tracking-widest ">Active/Pending</span>
                <div className={`text-3xl font-black ${counts.pending > 0 ? 'text-slate-900' : 'text-slate-700'} tracking-tight mt-1`}>{counts.pending}</div>
              </div>
              <Clock className="w-5 h-5 text-amber-500 transition-colors duration-300" />
            </div>
            <div className="mt-4 flex items-center gap-1.5 text-[10px] font-bold text-gray-400 uppercase">
              PENDING APPROVAL
            </div>
          </div>
        </Link>

        {/* Completed/Activated */}
        <Link to="/reports?status=Activated" className="group bg-white rounded-[16px] border border-slate-200 p-5 shadow-sm hover:shadow-md hover:border-[#8dc63f]/30 transition-all duration-300 relative overflow-hidden flex flex-col justify-between">
          <div className="relative z-10">
            <div className="flex items-start justify-between">
              <div>
                <span className="text-[11px] font-bold text-slate-600 uppercase tracking-widest">Completed</span>
                <div className="text-3xl font-black text-slate-900 tracking-tight mt-1">{counts.activated}</div>
              </div>
              <CheckCircle2 className="w-5 h-5 text-[#8dc63f] transition-colors duration-300" />
            </div>
            <div className="mt-4 flex items-center gap-1.5 text-[10px] font-bold text-slate-400 uppercase">
              Activated
            </div>
          </div>
        </Link>

        {/* Rejected */}
        <Link to="/reports?status=Rejected" className="group bg-white rounded-[16px] border border-slate-200 p-5 shadow-sm hover:shadow-md hover:border-rose-200/80 transition-all duration-300 relative overflow-hidden flex flex-col justify-between">
          <div className="relative z-10">
            <div className="flex items-start justify-between">
              <div>
                <span className="text-[11px] font-bold text-slate-600 uppercase tracking-widest">Rejected</span>
                <div className="text-3xl font-black text-slate-900 tracking-tight mt-1">{counts.rejected}</div>
              </div>
              <XCircle className="w-5 h-5 text-rose-500 transition-colors duration-300" />
            </div>
            <div className="mt-4 flex items-center gap-1.5 text-[10px] font-bold text-slate-400 uppercase">
              Compliance Filtered
            </div>
          </div>
        </Link>

        {/* Total Requests */}
        <Link to="/reports" className="group bg-white rounded-[16px] border border-slate-200 p-5 shadow-sm hover:shadow-md hover:border-slate-300/80 transition-all duration-300 relative overflow-hidden flex flex-col justify-between">
          <div className="relative z-10">
            <div className="flex items-start justify-between">
              <div>
                <span className="text-[11px] font-bold text-slate-600 uppercase tracking-widest">Total Requests</span>
                <div className="text-3xl font-black text-slate-900 tracking-tight mt-1">{counts.total}</div>
              </div>
              <FileText className="w-5 h-5 text-gray-500 transition-colors duration-300" />
            </div>
            <div className="mt-4 flex items-center gap-1.5 text-[10px] font-bold text-slate-400 uppercase">
              LIFETIME THROUGHPUT
            </div>
          </div>
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Trend Visualization */}
        <div className="lg:col-span-2 bg-white rounded-[16px] border border-slate-200 p-8 shadow-sm relative group overflow-hidden">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <div>
                <h3 className="text-lg font-bold text-slate-800 tracking-tight pl-10">Request Flux Analysis</h3>
                {/* <p className="text-xs text-slate-400 font-medium">Weekly volume trends</p> */}
              </div>
            </div>

          </div>

          <div className="h-[280px] -mx-2">
            {lineData && lineData[0]?.data?.length > 0 ? (
              <ResponsiveLine
                data={lineData}
                theme={CHART_THEME}
                margin={{ top: 20, right: 30, bottom: 60, left: 60 }}
                xScale={{ type: 'time', format: "%Y-%m-%d", precision: "day" }}
                yScale={{ type: 'linear', min: 'auto', max: 'auto', stacked: false }}
                curve="monotoneX"
                axisBottom={{
                  format: "%b %d",
                  tickSize: 4,
                  tickPadding: 16,
                  tickValues: `every ${tickValue} days`
                }}
                axisLeft={{
                  tickSize: 0,
                  tickPadding: 16,
                  tickValues: 4
                }}
                colors={['#8dc63f']}
                lineWidth={3}
                enablePoints={true}
                pointSize={8}
                pointColor="#ffffff"
                pointBorderWidth={2.5}
                pointBorderColor="#8dc63f"
                useMesh={true}
                enableArea={true}
                areaOpacity={0.08}
                enableGridX={false}
                enableGridY={true}
                crosshairType="cross"
              />
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-slate-400 bg-slate-50/50 rounded-[20px] border border-dashed border-slate-200">
                <TrendingUp className="w-12 h-12 mb-4 opacity-10" />
                <p className="text-sm font-bold uppercase tracking-widest pb-1 opacity-40">Insufficient Historical Data</p>
                <p className="text-xs">Gathering more requests for trend generation</p>
              </div>
            )}
          </div>
        </div>

        {/* Distribution Breakdown */}
        <Link to="/reports" className="block bg-white rounded-[16px] border border-slate-200 p-8 shadow-sm relative group hover:border-[#8dc63f]/30 hover:shadow-md transition-all duration-300">
          <div className="flex items-center gap-4 mb-8">
            <div className="p-2.5 bg-[#8dc63f]/10 rounded-[12px] group-hover:bg-[#8dc63f]/20 transition-colors">
              <PieChartIcon className="w-5 h-5 text-[#8dc63f]" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-800 tracking-tight group-hover:text-[#8dc63f] transition-colors">Access Types</h3>
              <p className="text-xs text-slate-400 font-medium">Request categorization</p>
            </div>
            {/* <ArrowUpRight className="w-4 h-4 text-slate-300 ml-auto opacity-0 group-hover:opacity-100 group-hover:text-[#8dc63f] transition-all transform group-hover:-translate-y-1 group-hover:translate-x-1" /> */}
          </div>

          <div className="h-[250px] relative">
            {pieData && pieData.length > 0 ? (
              <ResponsivePie
                data={pieData}
                theme={CHART_THEME}
                // margin={{ top: 20, right: 0, bottom: 100, left: 0 }}
                margin={{ top: 20, right: 0, bottom: 20, left: 0 }}
                innerRadius={0.7}
                padAngle={3}
                cornerRadius={12}
                activeOuterRadiusOffset={10}
                colors={['#8dc63f', '#76ab32', '#a3d853', '#c5e390', '#e0f0b8']}
                borderWidth={0}
                enableArcLinkLabels={false}
                enableArcLabels={false}

              />
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-slate-400 bg-slate-50/50 rounded-[20px] border border-dashed border-slate-200 text-center px-6">
                <Users className="w-12 h-12 mb-4 opacity-10" />
                <p className="text-sm font-bold uppercase tracking-widest pb-1 opacity-40">No Categorization data</p>
                <p className="text-xs">New requests will appear here once categorized</p>
              </div>
            )}

            {/* Center Label for Pie */}
            {pieData && pieData.length > 0 && (
              <div className="absolute top-40 left-1/2 transform -translate-x-1/2 -translate-y-1/2 -mt-10 text-center pointer-events-none">
                <div className="text-4xl font-black text-slate-900">{totalFromPie}</div>
                <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Volume</div>
              </div>
            )}
          </div>
        </Link>
      </div>
    </div>
  );
};

export default Dashboard;



